import { 
  collection, 
  doc, 
  addDoc, 
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { NOTIFICATION_STATUS, RESPONSE_TYPES } from '../constants/notificationConstants';

const COLLECTIONS = {
  NOTIFICATIONS: 'notifications',
  NOTIFICATION_RESPONSES: 'notificationResponses',
  NOTIFICATION_READ: 'notificationRead'
};

class NotificationService {
  // FIXED: Send notification with proper senderId handling
  async sendNotification(notificationData) {
    try {
      console.log('sendNotification called with data:', notificationData);
      
      // Ensure senderId is present
      if (!notificationData.senderId) {
        throw new Error('senderId is required but not provided');
      }
      
      const batch = writeBatch(db);
      
      // Create notification document
      const notificationRef = doc(collection(db, COLLECTIONS.NOTIFICATIONS));
      const notification = {
        ...notificationData,
        senderId: notificationData.senderId, // Explicitly set senderId
        status: NOTIFICATION_STATUS.SENT,
        sentAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        attachments: [] // Initialize empty, will be updated after file upload
      };
      
      console.log('Saving notification with senderId:', notification.senderId);
      
      batch.set(notificationRef, notification);
      
      // Create pending response documents for each recipient
      notificationData.recipientIds.forEach(userId => {
        const responseRef = doc(collection(db, COLLECTIONS.NOTIFICATION_RESPONSES));
        batch.set(responseRef, {
          notificationId: notificationRef.id,
          userId: userId,
          response: RESPONSE_TYPES.PENDING,
          createdAt: serverTimestamp()
        });
      });
      
      await batch.commit();
      
      console.log('Notification sent successfully with ID:', notificationRef.id);
      return { id: notificationRef.id, ...notification };
    } catch (error) {
      console.error('Error sending notification:', error);
      throw new Error('Failed to send notification: ' + error.message);
    }
  }

  // UPDATED: Improved upload attachment method
  async uploadAttachment(file, notificationId) {
    try {
      if (!notificationId) {
        throw new Error('Notification ID is required for file upload');
      }

      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_'); // Sanitize filename
      const fileName = `${timestamp}_${sanitizedFileName}`;
      
      // Use the actual notification ID for the path
      const storagePath = `notifications/${notificationId}/${fileName}`;
      const storageRef = ref(storage, storagePath);
      
      console.log('Uploading file to path:', storagePath);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        name: file.name,
        fileName: fileName,
        url: downloadURL,
        size: file.size,
        type: file.type,
        uploadedAt: new Date(),
        storagePath: storagePath
      };
    } catch (error) {
      console.error('Error uploading attachment:', error);
      throw new Error(`Failed to upload attachment: ${error.message}`);
    }
  }

  // NEW: Update notification with attachments after upload
  async updateNotificationAttachments(notificationId, attachments) {
    try {
      if (!notificationId) {
        throw new Error('Notification ID is required');
      }

      const notificationRef = doc(db, COLLECTIONS.NOTIFICATIONS, notificationId);
      await updateDoc(notificationRef, {
        attachments: attachments,
        updatedAt: serverTimestamp()
      });
      
      console.log('Notification updated with attachments successfully');
      return attachments;
    } catch (error) {
      console.error('Error updating notification attachments:', error);
      throw new Error('Failed to update notification with attachments');
    }
  }

  // Get notifications for a user
  async getUserNotifications(userId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.NOTIFICATIONS),
        where('recipientIds', 'array-contains', userId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const notifications = [];
      
      for (const docSnap of querySnapshot.docs) {
        const notification = {
          id: docSnap.id,
          ...docSnap.data(),
          createdAt: docSnap.data().createdAt?.toDate(),
          sentAt: docSnap.data().sentAt?.toDate()
        };
        
        // Get response for this notification
        const response = await this.getUserResponse(notification.id, userId);
        notification.userResponse = response;
        
        // Check if read
        const isRead = await this.isNotificationRead(notification.id, userId);
        notification.isRead = isRead;
        
        notifications.push(notification);
      }
      
      return notifications;
    } catch (error) {
      console.error('Error fetching user notifications:', error);
      throw new Error('Failed to fetch notifications');
    }
  }

  // FIXED: Get notifications sent by admin - for history view
  async getSentNotifications(senderId) {
    try {
      console.log('Fetching sent notifications for senderId:', senderId);
      
      // First, let's try to get all notifications to debug
      const allNotificationsQuery = query(
        collection(db, COLLECTIONS.NOTIFICATIONS),
        orderBy('createdAt', 'desc')
      );
      
      const allNotificationsSnapshot = await getDocs(allNotificationsQuery);
      console.log('Total notifications in database:', allNotificationsSnapshot.size);
      
      // Log all notifications for debugging
      allNotificationsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        console.log('Notification:', {
          id: doc.id,
          senderId: data.senderId,
          type: data.type,
          content: data.content?.substring(0, 50) + '...'
        });
      });
      
      // Now filter by senderId if provided
      let querySnapshot;
      if (senderId) {
        const q = query(
          collection(db, COLLECTIONS.NOTIFICATIONS),
          where('senderId', '==', senderId),
          orderBy('createdAt', 'desc')
        );
        querySnapshot = await getDocs(q);
        console.log(`Found ${querySnapshot.size} notifications for senderId: ${senderId}`);
      } else {
        // If no senderId, return all notifications
        querySnapshot = allNotificationsSnapshot;
        console.log('No senderId provided, returning all notifications');
      }
      
      const notifications = [];
      
      for (const docSnap of querySnapshot.docs) {
        const notificationData = docSnap.data();
        console.log('Processing notification:', docSnap.id, notificationData);
        
        const notification = {
          id: docSnap.id,
          ...notificationData,
          createdAt: notificationData.createdAt?.toDate(),
          sentAt: notificationData.sentAt?.toDate()
        };
        
        // Get all responses for this notification
        const responses = await this.getNotificationResponses(notification.id);
        notification.responses = responses;
        
        // Calculate response statistics
        const total = notification.recipientIds?.length || 0;
        const responseStats = {
          total,
          accept: responses.filter(r => r.response === RESPONSE_TYPES.ACCEPT).length,
          reject: responses.filter(r => r.response === RESPONSE_TYPES.REJECT).length,
          tentative: responses.filter(r => r.response === RESPONSE_TYPES.TENTATIVE).length,
          pending: total - responses.filter(r => r.response !== RESPONSE_TYPES.PENDING).length
        };
        responseStats.responseRate = total > 0 ? Math.round(((total - responseStats.pending) / total) * 100) : 0;
        
        notification.stats = responseStats;
        
        notifications.push(notification);
      }
      
      console.log('Returning notifications:', notifications.length);
      return notifications;
    } catch (error) {
      console.error('Error fetching sent notifications:', error);
      throw new Error('Failed to fetch sent notifications');
    }
  }

  // FIXED: Get notification history with enhanced details for admin  
  async getNotificationHistory(senderId) {
    try {
      console.log('getNotificationHistory called with senderId:', senderId);
      
      // TEMPORARY FIX: Since existing notifications have senderId: undefined,
      // let's load all notifications for now
      return await this.getSentNotifications(null); // Pass null to get all notifications
    } catch (error) {
      console.error('Error fetching notification history:', error);
      throw new Error('Failed to fetch notification history');
    }
  }

  // Get detailed notification status for a specific notification
  async getNotificationDetailedStatus(notificationId) {
    try {
      // Get notification details
      const notificationDoc = await getDoc(doc(db, COLLECTIONS.NOTIFICATIONS, notificationId));
      if (!notificationDoc.exists()) {
        throw new Error('Notification not found');
      }
      
      const notification = {
        id: notificationDoc.id,
        ...notificationDoc.data(),
        createdAt: notificationDoc.data().createdAt?.toDate(),
        sentAt: notificationDoc.data().sentAt?.toDate()
      };
      
      // Get all responses
      const responses = await this.getNotificationResponses(notificationId);
      
      // Get read status for all recipients
      const readStatuses = await this.getAllReadStatuses(notificationId);
      
      return {
        notification,
        responses,
        readStatuses
      };
    } catch (error) {
      console.error('Error fetching detailed notification status:', error);
      throw new Error('Failed to fetch notification status details');
    }
  }

  // Get read statuses for all recipients of a notification
  async getAllReadStatuses(notificationId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.NOTIFICATION_READ),
        where('notificationId', '==', notificationId)
      );
      
      const querySnapshot = await getDocs(q);
      const readStatuses = {};
      
      querySnapshot.forEach(doc => {
        const data = doc.data();
        readStatuses[data.userId] = {
          readAt: data.readAt?.toDate(),
          isRead: true
        };
      });
      
      return readStatuses;
    } catch (error) {
      console.error('Error fetching read statuses:', error);
      return {};
    }
  }

  // Respond to notification
  async respondToNotification(notificationId, userId, response) {
    try {
      const q = query(
        collection(db, COLLECTIONS.NOTIFICATION_RESPONSES),
        where('notificationId', '==', notificationId),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Update existing response
        const responseDoc = querySnapshot.docs[0];
        await updateDoc(responseDoc.ref, {
          response: response,
          respondedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } else {
        // Create new response
        await addDoc(collection(db, COLLECTIONS.NOTIFICATION_RESPONSES), {
          notificationId: notificationId,
          userId: userId,
          response: response,
          respondedAt: serverTimestamp(),
          createdAt: serverTimestamp()
        });
      }
      
      console.log('Response updated successfully');
    } catch (error) {
      console.error('Error responding to notification:', error);
      throw new Error('Failed to respond to notification');
    }
  }

  // Get user response for a notification
  async getUserResponse(notificationId, userId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.NOTIFICATION_RESPONSES),
        where('notificationId', '==', notificationId),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const responseData = querySnapshot.docs[0].data();
        return {
          id: querySnapshot.docs[0].id,
          ...responseData,
          respondedAt: responseData.respondedAt?.toDate(),
          createdAt: responseData.createdAt?.toDate()
        };
      }
      
      return { response: RESPONSE_TYPES.PENDING };
    } catch (error) {
      console.error('Error fetching user response:', error);
      return { response: RESPONSE_TYPES.PENDING };
    }
  }

  // Get all responses for a notification
  async getNotificationResponses(notificationId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.NOTIFICATION_RESPONSES),
        where('notificationId', '==', notificationId)
      );
      
      const querySnapshot = await getDocs(q);
      const responses = [];
      
      querySnapshot.forEach(doc => {
        const responseData = doc.data();
        responses.push({
          id: doc.id,
          ...responseData,
          respondedAt: responseData.respondedAt?.toDate(),
          createdAt: responseData.createdAt?.toDate()
        });
      });
      
      return responses;
    } catch (error) {
      console.error('Error fetching notification responses:', error);
      return [];
    }
  }

  // Mark notification as read
  async markAsRead(notificationId, userId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.NOTIFICATION_READ),
        where('notificationId', '==', notificationId),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        await addDoc(collection(db, COLLECTIONS.NOTIFICATION_READ), {
          notificationId: notificationId,
          userId: userId,
          readAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  // Check if notification is read
  async isNotificationRead(notificationId, userId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.NOTIFICATION_READ),
        where('notificationId', '==', notificationId),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking read status:', error);
      return false;
    }
  }

  // Get unread count for user
  async getUnreadCount(userId) {
    try {
      const notifications = await this.getUserNotifications(userId);
      return notifications.filter(n => !n.isRead).length;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  // Get notification statistics for admin dashboard
  async getNotificationStatistics(senderId) {
    try {
      const notifications = await this.getSentNotifications(senderId);
      
      const stats = {
        total: notifications.length,
        byType: {
          general: notifications.filter(n => n.type === 'general').length,
          invitation: notifications.filter(n => n.type === 'invitation').length,
          meeting: notifications.filter(n => n.type === 'meeting').length
        },
        totalRecipients: notifications.reduce((sum, n) => sum + (n.recipientIds?.length || 0), 0),
        totalResponses: notifications.reduce((sum, n) => sum + (n.responses?.length || 0), 0),
        averageResponseRate: notifications.length > 0 
          ? Math.round(notifications.reduce((sum, n) => sum + (n.stats?.responseRate || 0), 0) / notifications.length)
          : 0
      };
      
      return stats;
    } catch (error) {
      console.error('Error getting notification statistics:', error);
      throw new Error('Failed to get notification statistics');
    }
  }

  // Delete attachment
  async deleteAttachment(filePath) {
    try {
      const fileRef = ref(storage, filePath);
      await deleteObject(fileRef);
      console.log('Attachment deleted successfully');
    } catch (error) {
      console.error('Error deleting attachment:', error);
    }
  }
}

export const notificationService = new NotificationService();